import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { adminService } from '../../../services/adminService';

const UseCaseManagementPanel = ({ useCases, onRefresh }) => {
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', icon: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleEdit = (useCase) => {
    setEditingId(useCase?.id);
    setFormData({
      name: useCase?.name || '',
      description: useCase?.description || '',
      icon: useCase?.icon || ''
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({ name: '', description: '', icon: '' });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({ name: '', description: '', icon: '' });
    setError(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        await adminService?.updateUseCase(editingId, formData);
      } else {
        await adminService?.createUseCase(formData);
      }

      await onRefresh();
      handleCancel();
    } catch (err) {
      console.error('Error saving use case:', err);
      setError(err?.message || 'Failed to save use case');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this use case? This will also remove all associated relationships.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await adminService?.deleteUseCase(id);
      await onRefresh();
    } catch (err) {
      console.error('Error deleting use case:', err);
      setError(err?.message || 'Failed to delete use case');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Use Cases</h2>
        <Button
          variant="default"
          size="sm"
          onClick={handleAdd}
          iconName="Plus"
          iconPosition="left"
          disabled={isAdding || editingId}
        >
          Add Use Case
        </Button>
      </div>

      {error && (
        <div className="mb-4 bg-destructive/10 border border-destructive rounded-lg p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Add Form */}
      {isAdding && (
        <div className="mb-6 bg-muted/50 border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Add New Use Case</h3>
          <div className="space-y-4">
            <Input
              label="Name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              placeholder="e.g., Text, Code, Image"
              required
            />
            <Input
              label="Description"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder="Brief description of the use case"
            />
            <Input
              label="Icon"
              value={formData?.icon}
              onChange={(e) => setFormData({ ...formData, icon: e?.target?.value })}
              placeholder="e.g., FileText, Code, Image"
              description="Lucide icon name"
            />
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                loading={loading}
                disabled={!formData?.name}
              >
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Use Cases List */}
      <div className="space-y-3">
        {useCases?.map((useCase) => (
          <div
            key={useCase?.id}
            className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            {editingId === useCase?.id ? (
              <div className="space-y-4">
                <Input
                  label="Name"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  required
                />
                <Input
                  label="Description"
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                />
                <Input
                  label="Icon"
                  value={formData?.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e?.target?.value })}
                  description="Lucide icon name"
                />
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSave}
                    loading={loading}
                    disabled={!formData?.name}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {useCase?.icon && (
                    <div className="flex-shrink-0 mt-1">
                      <Icon name={useCase?.icon} size={24} className="text-primary" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">{useCase?.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{useCase?.description}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Link" size={14} />
                      <span>{useCase?.aiTools?.length || 0} AI tools linked</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(useCase)}
                    iconName="Edit"
                    disabled={loading || isAdding || editingId}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(useCase?.id)}
                    iconName="Trash2"
                    disabled={loading || isAdding || editingId}
                    className="text-destructive hover:text-destructive"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {useCases?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Layers" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No use cases found. Add your first use case to get started.</p>
        </div>
      )}
    </div>
  );
};

export default UseCaseManagementPanel;